/**
 * A manager class for maintaining state across different views with session storage.
 */
class SessionManager {
    static #route = null
    static #group = ''

    /**
     * Creates a SessionManager instance for accessing the non-static set(), get() methods.
     * 
     * By making get and set non-static, we ensure that #route, #group are properly set through the constructor whenever either is accessed.
     * 
     * @param {route} route The vue route object for the given vue instance. 
     */
    constructor(route) {
        SessionManager.navigate(SessionManager.#route, route)
    }

    /**
     * @param {string} key A user-chosen key to save some view state under.
     * 
     * @returns {string} The composite key including route path to save and access session state values. 
     */
    static #getKey(key) {
        return `${this.#group}-${key}`
    }

    /**
     * @param {string} key The key to store data under
     * @param {any} value The value to store under the key passed
     * @param {boolean} strict If true, does not allow existing keys to be over-written
     * 
     * @returns {boolean} Returns false if an overwrite is attempted in strict mode
     */
    set(key, value, strict=false) {
        key = SessionManager.#getKey(key)
        if (strict && sessionStorage.getItem(key)) return false

        sessionStorage.setItem(key, JSON.stringify(value))
        return true
    }

    /**
     * @param {string} key The key to access some session value under
     * 
     * @returns {object} The value under the key, or null if none exists
     */
    get(key) {
        let value = sessionStorage.getItem(SessionManager.#getKey(key))
        if (value) value = JSON.parse(value)
        return value
    }

    /**
     * Clears the keys associated to a given route.
     * 
     * @param {object} from The previous vue route object.
     * @param {object} to The next vue route object.
     * @param {boolean} checkMatches If true, will only clear the route passed when it does not share the current root.
     */
    static navigate(from, to, checkMatches=true) {
        if (!to) to = from
        const toGroup = to.meta && to.meta.group ? to.meta.group : to.path.split('/')[1]
        
        // Check that 'from' is nullish, and not just a dashboard navigation
        if (from && !(from.path === '/' && !from.name)) {
            const fromGroup = from.meta && from.meta.group ? from.meta.group : from.path.split('/')[1]
            if (checkMatches && fromGroup === toGroup) return
            this.clearGroup(fromGroup)
        }
        

        this.#route = to
        this.#group = toGroup
    }

    /**
     * Clears the keys prepended by a given group.
     * 
     * @param {string} group 
     */
    static clearGroup(group) {
        Object.keys(sessionStorage).forEach(key => {
            if (key.startsWith(group)) sessionStorage.removeItem(key)
        })
    }
}

export default SessionManager